import React, { FC, useCallback, useMemo, useState } from 'react';
import { ConnectedProps, connect } from 'react-redux';
import { Field, InjectedFormProps, reduxForm } from 'redux-form';

import { saveProject } from '@/actions/project';
import { notify } from '@/actions/ui';
import Button from '@/components/Button/Button';
import ConsultationsStarting from '@/components/ConsultationsStarting';
import EditDialog, { EditDialogProps } from '@/components/EditDialog/EditDialog';
import EditIcon from '@/components/EditIcon';
import SettingsMenu from '@/components/EditProjectMembers/SettingsMenu';
import { TextField } from '@/components/FormAdapters/FormAdapters';
import LayoutPage from '@/components/Layout/LayoutPage';
import Link from '@/components/Link';
import Markdown from '@/components/Markdown';
import MediaQuery from '@/components/MediaQuery';
import MemberList from '@/components/MemberList';
import MemberRequests from '@/components/MemberRequests/Project';
import { apiErrorMessage } from '@/core/api';
import { sortMembers } from '@/core/project';
import ExpertRequestPreview from '@/expertrequest/components/ExpertRequestPreview';
import ProjectEngagementsSection from '@/features/consulting-engagements/live/ProjectEngagementsSection';
import { RootState } from '@/store';
import { SCREEN_XS } from '@/theme/screens';
import { normalizeSpace } from '@/utils';

import s from './ProjectDetails.module.scss';

const dialogs = {
  name: {
    title: 'Edit Name',
    component() {
      return (
        <Field
          changeOnBlur={false}
          id="projectName"
          component={TextField}
          inputProps={{ maxLength: 255 }}
          name="name"
          fullWidth
          label="Name this project"
        />
      );
    },
  },
  tracking_code: {
    title: 'Edit Tracking Code',
    component() {
      return (
        <Field
          changeOnBlur={false}
          id="projectTrackingCode"
          component={TextField}
          inputProps={{ maxLength: 60 }}
          name="tracking_code"
          fullWidth
          label="Tracking Code"
          multiline
          onKeyDown={(e: any) => e.keyCode === 13 && e.preventDefault()}
          parse={(v: string) => normalizeSpace(v)}
        />
      );
    },
  },
};

type DialogOption = keyof typeof dialogs;
interface ProjectDetailsProps {
  projectId: string;
}

const connector = connect(
  (state: RootState, ownProps: ProjectDetailsProps) => {
    return {
      viewer: state.viewer,
      project: (state.projects.default.edges.find((e) => e.node.id === ownProps.projectId) || {})
        .node,
    };
  },
  {
    notify,
  }
);

const ProjectDetails: FC<ProjectDetailsProps & ConnectedProps<typeof connector>> = ({
  viewer,
  project,
  notify,
}) => {
  const [openedDialog, setOpenedDialog] = useState<DialogOption>();

  const closeDialog = useCallback(
    (message?: string, error?: boolean) => {
      setOpenedDialog(undefined);

      if (message) {
        notify(message, error ? 'error' : 'success');
      }
    },
    [notify]
  );

  const members = useMemo(() => sortMembers(project?.members || []), [project?.members]);
  const membersToShow = useMemo(() => members.filter((m: any) => m.state === 'active'), [members]);
  const memberRequests = useMemo(
    () => members.filter((m: any) => m.state === 'awaiting_approval'),
    [members]
  );

  const none = <span className={s.none}>None</span>;

  if (!project) return <div />;

  return (
    <LayoutPage showNav selected="expert_requests">
      <ConsultationsStarting />
      <div className={s.header}>
        <div className={s.title}>
          <div className={s.titleText}>
            {project.name}
            <EditIcon
              onClick={() => setOpenedDialog('name')}
              style={{ marginLeft: 10, verticalAlign: 'middle' }}
            />
          </div>
          <div className={s.members}>
            <MemberList members={membersToShow} />
            <SettingsMenu project={project} />
          </div>
        </div>
        {viewer.admin && (
          <div className={s.field}>
            <div className={s.fieldLabel}>Team:</div>
            <div className={s.fieldValue}>
              {project.group ? <Link to={project.group.html_url}>{project.group.name}</Link> : none}
            </div>
          </div>
        )}
        <div className={s.field}>
          <div className={s.fieldLabel}>Tracking Code:</div>
          <div className={s.fieldValue}>{project.tracking_code || none}</div>
          <EditIcon onClick={() => setOpenedDialog('tracking_code')} style={{ marginLeft: 10 }} />
        </div>
      </div>

      <div>
        <MemberRequests projectId={project.id} memberRequests={memberRequests} />
      </div>

      <div className={s.requests}>
        {project.expert_requests.map((er: any) => (
          <ExpertRequestPreview
            // @ts-ignore
            className={s.request}
            key={er.id}
            request={er}
            project={project}
            showState
            showContextActions
          />
        ))}
      </div>

      <Link
        to={`/request_expert?project_id=${project.id}`}
        style={{ display: 'inline-block', marginTop: 20 }}
      >
        <Button color="lightTan" size="large">
          Add Expert Request
        </Button>
      </Link>

      <Markdown body={project.summary} />

      {/* After the summary: what the project is, then what is happening on it. */}
      <ProjectEngagementsSection projectId={project.id} />

      {(Object.keys(dialogs) as DialogOption[]).map((dialog) => (
        <EditProject
          key={dialog}
          initialValues={project}
          open={openedDialog === dialog}
          onClose={closeDialog}
          component={dialogs[dialog].component}
          title={dialogs[dialog].title}
        />
      ))}
    </LayoutPage>
  );
};

interface FormData {
  id: string;
}

interface EditProjectProps extends Omit<EditDialogProps, 'onClose'> {
  component: React.ComponentType<any>;
  children?: React.ReactNode;
  onClose: (message?: string, error?: boolean) => void;
  submitOnClick?: boolean;
}

const editProjectConnector = connect(
  (_state: RootState, ownProps: EditProjectProps) => {
    return {
      // @ts-ignore
      validate: ownProps.component.validate,
    };
  },
  {
    saveProject,
  }
);

const EditProjectBare: FC<
  EditProjectProps &
    InjectedFormProps<FormData, EditProjectProps> &
    ConnectedProps<typeof editProjectConnector>
> = ({
  onClose,
  reset,
  submitOnClick = false,
  handleSubmit,
  component,
  array,
  change,
  children,
  saveProject,
  ...props
}) => {
  const myHandleSubmit = useCallback(
    async (data: FormData) => {
      try {
        const result = await saveProject(data);
        if (result) {
          reset();
          onClose('Project updated.', false);
        }
      } catch (e) {
        console.error(e);
        onClose(apiErrorMessage(e, 'An error occurred when updating the project'), true);
      }
    },
    [onClose, reset, saveProject]
  );

  const handleCloseRequest = useCallback(() => onClose(), [onClose]);

  return (
    <EditDialog
      onSubmit={handleSubmit(myHandleSubmit)}
      onClose={handleCloseRequest}
      width={SCREEN_XS}
      {...props}
    >
      <MediaQuery maxWidth={SCREEN_XS}>
        {(isMobileVersion: boolean) =>
          component
            ? React.createElement(component, {
                array,
                change,
                isMobileVersion,
                onClick: submitOnClick ? handleSubmit(myHandleSubmit) : undefined,
              })
            : children
        }
      </MediaQuery>
    </EditDialog>
  );
};

const EditProject = reduxForm<FormData, EditProjectProps>({
  form: 'editProject',
  enableReinitialize: true,
})(editProjectConnector(EditProjectBare));

export default connector(ProjectDetails);
